import { useState, useEffect, useRef } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  InputAdornment,
  IconButton,
  Box,
  Typography,
} from '@mui/material';
import { GridClearIcon } from '@mui/x-data-grid';
import { useAppDispatch } from 'store/hooks';
import { getPatientSearchList } from 'store/slices/secureMessageSlice';

export interface PatientOption {
  id: number;
  label: string;
  dob?: string;
}

interface PatientSearchBoxProps {
  onSelect: (patient: PatientOption) => void;
  onTyping?: () => void;
  error?: boolean;
  helperText?: string;
}

export default function PatientSearchBox(props: PatientSearchBoxProps) {
  const dispatch = useAppDispatch();

  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<PatientOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] =
    useState<PatientOption | null>(null);
  const [open, setOpen] = useState(false);
  const skipSearchRef = useRef(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const q = inputValue.trim();

    if (q.length < 3) {
      setOptions([]);
      setOpen(false);
      return;
    }

    setLoading(true);
    const requestId = ++requestIdRef.current;

    dispatch(getPatientSearchList(q))
      .unwrap()
      .then((response: any) => {
      // Ignore stale requests
        if (requestId !== requestIdRef.current) return;

        const data = response?.data || [];

        // Strong deduplication + sorting (optional)
        const uniqueMap = new Map<number, PatientOption>();

        data.forEach((item: any) => {
          if (!uniqueMap.has(item.patientId)) {
            uniqueMap.set(item.patientId, {
              id: item.patientId,
              label: item.fullName,
              dob: item.dob,
            });
          }
        });

        const uniqueList = Array.from(uniqueMap.values());

        setOptions(uniqueList);
        setOpen(uniqueList.length > 0);
      })
      .catch(() => {
        setOptions([]);
        setOpen(false);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [inputValue, dispatch]);

  return (
    <Autocomplete<PatientOption,  false,false, false>
      options={options}
      value={selectedOption}
      inputValue={inputValue}
      loading={loading}
      open={open}
      fullWidth
      openOnFocus
      filterOptions={(x) => x}
      noOptionsText="No patients found"
      isOptionEqualToValue={(o, v) => o.id === v.id}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.label
      }
      onOpen={() => {
        if (options.length > 0) setOpen(true);
      }}
      onClose={() => setOpen(false)}
      onInputChange={(_, value, reason) => {
        if (reason === 'clear') {
          setOptions([]);
          setInputValue('');
          setSelectedOption(null);
          props.onTyping?.();
          return;
        }

        // ignore reset from option selection
        if (reason === 'reset') {
          return;
        }

        // clear previous selected chip/text
        if (selectedOption) {
          setSelectedOption(null);
        }

        setInputValue(value);
        setOptions([]);
        props.onTyping?.();
      }}
     
      onChange={(_, value) => {
        if (!value) return;

        skipSearchRef.current = true;

        setSelectedOption(value);

        // clear typed text
        setInputValue('');

        setOpen(false);

        props.onSelect(value);
      }}
      renderOption={(propsOption, option) => (
        <Box component="li" {...propsOption}  >
          <Box>
            <Typography fontSize={14}>{option.label}</Typography>
            {option.dob && (
              <Typography fontSize={12} color="text.secondary">
                DOB: {option.dob}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder={
            selectedOption
              ? ''
              : 'Type patient name to search'
          }
          error={props.error}
          helperText={props.helperText}
          InputProps={{
            ...params.InputProps,
            startAdornment:
              selectedOption && !open ? (
                <Box sx={{ ml: 1 }}>
                  <Typography fontSize={12}>
                    {selectedOption.label}
                  </Typography>

                  {selectedOption.dob && (
                    <Typography
                      fontSize={10}
                      color="text.secondary"
                    >
                      DOB: {selectedOption.dob}
                    </Typography>
                  )}
                </Box>
              ) : null,

            endAdornment: (
              <>
                {loading && <CircularProgress size={18} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}